import React from 'react';
import {
  Typography,
  Box,
  Grid,
  useTheme,
  Card,
  Divider
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import EmptyState from '../EmptyState';
import { formatCurrency, formatMonthYear, formatNumber, formatYAxisValue } from './formatters';

interface LineDataPoint {
  x: string;
  y: number;
  transacoes?: number;
  transacoes_credito?: number;
  transacoes_debito?: number;
  quantidade?: number;
  credito?: number;
}

interface LineDataSeries {
  id: string;
  data: LineDataPoint[];
}

interface DrillDownEvolutionChartProps {
  lineData: LineDataSeries[];
  title?: string;
}

const DrillDownEvolutionChart: React.FC<DrillDownEvolutionChartProps> = ({ 
  lineData, 
  title = "Evolução Mensal das Transações" 
}) => {
  const theme = useTheme();

  const hasRealData = lineData.some(series => 
    series.data.some(point => point.y > 0)
  );

  return (
    <Grid item xs={12}>
      <Typography 
        variant="subtitle1"
        align="center"
        sx={{ color: theme.palette.text.secondary, mb: 2 }}
      >
        {title}
      </Typography>
      <Box sx={{ height: 400 }}>
        {hasRealData ? (
          <ResponsiveLine
            data={lineData}
            margin={{ top: 50, right: 110, bottom: 120, left: 100 }}
            xScale={{ type: 'point' }}
            yScale={{ 
              type: 'linear', 
              min: 'auto', 
              max: 'auto',
              stacked: false,
              reverse: false
            }}
            curve="catmullRom"
            axisTop={null}
            axisRight={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: '',
              legendOffset: 0,
              format: (value) => formatYAxisValue(value as number)
            }}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Período',
              legendOffset: 50,
              legendPosition: 'middle'
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              legend: 'Valor (R$)',
              legendOffset: -80,
              legendPosition: 'middle',
              format: (value) => {
                const absValue = Math.abs(value as number);
                return absValue >= 1000000 
                  ? `${(absValue / 1000000).toFixed(1)}M`
                  : absValue >= 1000 
                  ? `${(absValue / 1000).toFixed(1)}K`
                  : formatCurrency(value as number);
              }
            }}
            pointSize={8}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={3}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            colors={[theme.palette.error.main, theme.palette.success.main]}
            lineWidth={3}
            enableArea={false}
            enableGridX={true}
            enableGridY={true}
            gridYValues={5}
            gridXValues={lineData[0]?.data?.length || 0}
            useMesh={true}
            crosshairType="cross"
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <Card sx={{ p: 2, minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📅 {String(slice.points[0]?.data?.x)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {slice.points.map((point) => (
                  <Box key={point.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: point.serieColor,
                        mr: 1
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {point.serieId}
                      </Typography>
                      <Typography variant="h6" color={point.serieColor}>
                        {formatCurrency(point.data.y as number)}
                      </Typography>
                      {/* Mostrar transações separadas quando disponível */}
                      {(point.data as any).transacoes_credito !== undefined || (point.data as any).transacoes_debito !== undefined ? (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            🟢 {formatNumber((point.data as any).transacoes_credito || 0)} entradas
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            🔴 {formatNumber((point.data as any).transacoes_debito || 0)} saídas
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {/* Fallback para campo legado ou cálculo dinâmico */}
                          {(() => {
                            const transacoes = (point.data as any).transacoes;
                            const totalTransacoes = transacoes !== undefined 
                              ? transacoes 
                              : ((point.data as any).transacoes_credito || 0) + ((point.data as any).transacoes_debito || 0);
                            return `${formatNumber(totalTransacoes)} transações`;
                          })()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Card>
            )}
            animate={true}
            motionConfig="gentle"
          />
        ) : (
          <EmptyState
            icon="📈"
            title={
              lineData.length > 0 
                ? "Dados encontrados mas todos os valores estão zerados" 
                : "Nenhum dado de transações por período"
            }
            description={
              lineData.length > 0
                ? "Foram encontrados registros, mas todos os valores estão zerados."
                : "Não há dados disponíveis para exibir no gráfico de evolução."
            }
            height={400}
          />
        )}
      </Box>
    </Grid>
  );
};

export default DrillDownEvolutionChart; 